import React, { useState, useEffect } from 'react';
import {
    Table,
    Card,
    Button,
    Modal,
    Form,
    Input,
    DatePicker,
    Select,
    InputNumber,
    Row,
    Col,
    Typography,
    message,
    Spin,
    Popconfirm,
    Tag,
} from 'antd';
import { PlusOutlined, PrinterOutlined, EditOutlined, DeleteOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import {
    getPaymentVouchers,
    createPaymentVoucher,
    updatePaymentVoucher,
    deletePaymentVoucher,
    downloadPaymentVoucherPdf,
    getNextPaymentVoucherNumber
} from '../api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

export default function PaymentVouchers() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [editingId, setEditingId] = useState(null);
    const [printingId, setPrintingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await getPaymentVouchers();
            if (res.success) {
                setData(res.data);
            } else {
                message.error('Failed to fetch vouchers');
            }
        } catch (err) {
            message.error('Error fetching data');
        }
        setLoading(false);
    };

    const handleAdd = async () => {
        setEditingId(null);
        form.resetFields();
        form.setFieldsValue({
            date: dayjs(),
            current_date: dayjs(),
            payment_method: 'cash'
        });
        setModalVisible(true);
        try {
            const res = await getNextPaymentVoucherNumber();
            if (res.success) {
                form.setFieldsValue({ voucher_no: res.voucher_no });
            }
        } catch (error) {
            console.error("Failed to fetch next voucher number", error);
        }
    };

    const handleEdit = (record) => {
        setEditingId(record.id);
        form.setFieldsValue({
            ...record,
            date: record.date ? dayjs(record.date) : null,
            current_date: record.current_date ? dayjs(record.current_date) : null,
        });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const res = await deletePaymentVoucher(id);
            if (res.success) {
                message.success('Voucher deleted');
                fetchData();
            }
        } catch (err) {
            message.error('Delete failed');
        }
    };

    const handleVoucherAction = async (id, action) => {
        if (action === 'print' || action === 'download') setPrintingId(`${action}-${id}`);
        const hide = message.loading(action === 'view' ? 'Opening preview...' : `Processing ${action}...`, 0);
        try {
            await downloadPaymentVoucherPdf(id, action);
            hide();
        } catch (err) {
            hide();
            message.error(`${action} failed`);
        } finally {
            setPrintingId(null);
        }
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);
            const payload = {
                ...values,
                date: values.date ? values.date.format('YYYY-MM-DD') : null,
                current_date: values.current_date ? values.current_date.format('YYYY-MM-DD') : null,
            };

            let res;
            if (editingId) {
                res = await updatePaymentVoucher(editingId, payload);
            } else {
                res = await createPaymentVoucher(payload);
            }

            if (res.success) {
                message.success(editingId ? 'Voucher updated' : 'Voucher created');
                setModalVisible(false);
                fetchData();
            }
        } catch (err) {
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    const columns = [
        { title: 'Voucher No', dataIndex: 'voucher_no', key: 'voucher_no', width: 120 },
        { title: 'Date', dataIndex: 'date', key: 'date', render: d => dayjs(d).format('DD-MM-YYYY'), width: 120 },
        { title: 'Paid To', dataIndex: 'paid_to', key: 'paid_to' },
        { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: a => <b>{Number(a).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b>,
            width: 120
        },
        {
            title: 'Method',
            dataIndex: 'payment_method',
            key: 'payment_method',
            render: m => <Tag color={m === 'cash' ? 'green' : 'blue'}>{m.toUpperCase()}</Tag>,
            width: 100
        },
        {
            title: 'Action',
            key: 'action',
            width: 220,
            render: (_, record) => (
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                        icon={<EyeOutlined />}
                        onClick={() => handleVoucherAction(record.id, 'view')}
                        title="View Voucher"
                    />
                    <Button
                        icon={<DownloadOutlined />}
                        onClick={() => handleVoucherAction(record.id, 'download')}
                        loading={printingId === `download-${record.id}`}
                        disabled={printingId && printingId !== `download-${record.id}`}
                        title="Download Voucher"
                    />
                    <Button
                        icon={<PrinterOutlined />}
                        onClick={() => handleVoucherAction(record.id, 'print')}
                        loading={printingId === `print-${record.id}`}
                        disabled={printingId && printingId !== `print-${record.id}`}
                        title="Print Voucher"
                    />
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Popconfirm title="Delete this voucher?" onConfirm={() => handleDelete(record.id)}>
                        <Button icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                </div>
            )
        }
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <Title level={2}>Payment Vouchers</Title>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                    Create Payment Voucher
                </Button>
            </div>

            <Card bodyStyle={{ padding: 0 }}>
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 15 }}
                />
            </Card>

            <Modal
                title={editingId ? "Edit Payment Voucher" : "New Payment Voucher"}
                open={modalVisible}
                onOk={handleOk}
                onCancel={() => setModalVisible(false)}
                okText="Save"
                confirmLoading={submitting}
                width={700}
            >
                <Form form={form} layout="vertical" onValuesChange={(changed, all) => {
                    if (changed.price !== undefined || changed.vat_percent !== undefined) {
                        const price = Number(all.price || 0);
                        const vat = Number(all.vat_percent || 0);
                        form.setFieldsValue({ amount: price + (price * vat / 100) });
                    }
                }}>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="voucher_no" label="Voucher No">
                                <Input placeholder="Auto-generated if left blank" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="date" label="Voucher Date" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="current_date" label="Current Date (Bottom)">
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="paid_to" label="Paid to" rules={[{ required: true }]}>
                                <Input placeholder="Receiver Name" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="description" label="On Account of (Description)" rules={[{ required: true }]}>
                        <Input.TextArea rows={2} placeholder="Explain purpose of payment" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="price" label="Price (Excl. VAT)">
                                <InputNumber style={{ width: '100%' }} precision={2} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="vat_percent" label="VAT %">
                                <InputNumber style={{ width: '100%' }} min={0} max={100} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="vat_no" label="VAT No / TRN">
                                <Input placeholder="Optional" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="amount" label="Total Amount (Calculated)" rules={[{ required: true }]}>
                                <InputNumber style={{ width: '100%' }} prefix="AED" precision={2} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="payment_method" label="Payment Method" rules={[{ required: true }]}>
                                <Select>
                                    <Option value="cash">Cash</Option>
                                    <Option value="cheque">Cheque</Option>
                                    <Option value="transfer">Bank Transfer</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prev, curr) => prev.payment_method !== curr.payment_method}
                    >
                        {({ getFieldValue }) =>
                            getFieldValue('payment_method') === 'cheque' || getFieldValue('payment_method') === 'transfer' ? (
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name="cheque_no" label={getFieldValue('payment_method') === 'cheque' ? "Cheque No" : "Ref No"}>
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item name="bank" label="Bank Name">
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            ) : null
                        }
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
